import { Box, Grid, Typography } from '@material-ui/core'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BulkSettings, IdGame, PricingStrategy } from 'shared'
import { OptionBox } from '../OptionBox'
import { ProgressStepper } from '../ProgressStepper'
import { RestartButton } from '../RestartButton'
import { StepBox } from '../StepBox'
import { FinalizePresenter } from './FinalizePresenter'
import { ModifierPresenter } from './ModifierPresenter'

export interface PriceGuideFlowPresenterProps {
  newName: string
  onRestart: () => void
  bulkSettings: BulkSettings[]
  onDone: (strategy: PricingStrategy) => void
  dataTestid?: string
}

enum Step {
  Base = 1,
  Modifier,
  Finalize,
}

export const PriceGuideFlowPresenter = ({
  newName,
  onRestart,
  bulkSettings,
  onDone,
  dataTestid,
}: PriceGuideFlowPresenterProps): JSX.Element => {
  const [step, setStep] = useState<Step>(Step.Base)
  const [progress, setProgress] = useState<Step>(Step.Base)

  const [strategy, setStrategy] = useState<PricingStrategy>({
    _id: '2',
    name: newName,
    base: {
      kind: 'priceGuide',
      tactic: { parameter: 'AVG7' },
    },
    modifier: {
      kind: 'percent',
      value: 90,
    },
    bulkSettingsId: {
      [IdGame.Magic]: '',
      [IdGame.Yugioh]: '',
      [IdGame.Pokemon]: '',
      [IdGame.Vanguard]: '',
      [IdGame.DBS]: '',
      [IdGame.FF]: '',
      [IdGame.WS]: '',
      [IdGame.FAB]: '',
      [IdGame.Digimon]: '',
    },
  })

  const { t } = useTranslation()

  return (
    <Grid style={{ height: '100%' }} container>
      <Grid item xs={1}>
        <ProgressStepper
          activeStep={step}
          progress={progress}
          handleStep={(index) => setStep(index)}
          steps={[t('strategy.parameter'), t('strategy.modifier'), t('strategy.name')]}
        />
      </Grid>
      <Grid item xs={11}>
        <Box height="100%" padding={3}>
          <Typography variant="h4">
            {t('strategy.create.title')}{' '}
            <RestartButton onClick={onRestart} dataTestid={'restart-strategy-btn'} />
          </Typography>

          <Box height="inherit" paddingTop={1}>
            {
              {
                [Step.Base]: (
                  <StepBox>
                    <Typography variant="body1">
                      {t('strategy.create.parameterDescription')}
                    </Typography>

                    <Typography variant="caption">
                      {t('strategy.create.parameterDescription2')}{' '}
                    </Typography>
                    <Typography variant="caption">
                      {t('strategy.create.parameterDescription3')}{' '}
                    </Typography>

                    <Grid container>
                      {['Trend', 'AVG1', 'AVG7', 'AVG30'].map((param) => (
                        <Grid xs={3} key={param} item data-testid={`box-${param}`}>
                          <OptionBox
                            recommended={param === 'AVG7'}
                            onClick={() => {
                              const strategyCopy = { ...strategy }
                              strategyCopy.base.tactic.parameter = param
                              setStrategy(strategyCopy)
                              setStep(Step.Base + 1)
                              setProgress(Math.max(step + 1, progress))
                            }}
                            title={param}
                            width="130px"
                            dataTestid={dataTestid + `-${param}`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </StepBox>
                ),
                [Step.Modifier]: (
                  <StepBox
                    onNext={() => {
                      setStep(Step.Modifier + 1)
                      setProgress(Math.max(step + 1, progress))
                    }}
                  >
                    <ModifierPresenter
                      bulkSettings={bulkSettings}
                      strategy={strategy}
                      onChange={(strategy) => setStrategy(strategy)}
                    />
                  </StepBox>
                ),
                [Step.Finalize]: (
                  <StepBox
                    isLast
                    onNext={() => onDone(strategy)}
                    disableNext={strategy.name.trim() === ''}
                  >
                    <FinalizePresenter strategy={strategy} setStrategy={setStrategy} />
                  </StepBox>
                ),
              }[step]
            }
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}
